import React, { useState, useEffect } from 'react';
import { X, GitMerge, Check, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import C from '../../data/colors';
import { theme } from '../../data/colors';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MERGE_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'age_group', label: 'Age Group' },
  { key: 'dietary_requirements', label: 'Dietary Requirements' },
  { key: 'handicap_requirements', label: 'Accessibility Requirements' },
  { key: 'allergies', label: 'Allergies' },
  { key: 'table_name', label: 'Table Assignment' },
  { key: 'other_notes', label: 'Other Notes' },
];

const GuestMergeModal = ({ guestA, guestB, onClose, onMerged }) => {
  const [selections, setSelections] = useState({});
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');

  // Initialize selections - prefer guestA values by default, but use guestB if A is empty
  useEffect(() => {
    const initial = {};
    MERGE_FIELDS.forEach(f => {
      const valA = guestA?.[f.key];
      const valB = guestB?.[f.key];
      // Default to 'a' if A has value, otherwise 'b'
      initial[f.key] = (valA && String(valA).trim()) ? 'a' : 'b';
    });
    setSelections(initial);
  }, [guestA, guestB]);

  const handleSelect = (field, value) => {
    setSelections(prev => ({ ...prev, [field]: value }));
  };

  const handleMerge = async () => {
    setMerging(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/guests/merge`, {
        guest_id_a: guestA.id,
        guest_id_b: guestB.id,
        field_selections: selections,
      });
      onMerged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error merging guests');
    } finally {
      setMerging(false);
    }
  };

  if (!guestA || !guestB) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    }}>
      <div 
        data-testid="guest-merge-modal"
        style={{
          background: C.bgCard,
          borderRadius: 16,
          width: '100%',
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          border: `1px solid ${C.border}`,
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: `1px solid ${C.border}`,
          background: C.warningBg,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <GitMerge size={20} color={C.warning} />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C.warning }}>
                Resolve Duplicate
              </h2>
              <div style={{ fontSize: 12, color: C.warning }}>
                Select which value to keep for each field
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color={C.textSecondary} />
          </button>
        </div>

        {/* Comparison Table */}
        <div style={{ padding: 24 }}>
          {error && (
            <div style={{ 
              background: C.errorBg, 
              color: C.error, 
              padding: '12px 16px', 
              borderRadius: 8, 
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '140px 1fr 60px 1fr',
            gap: 0,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            {/* Header Row */}
            <div style={{ ...headerCellStyle, background: C.bgPrimary }}>Field</div>
            <div style={{ ...headerCellStyle, background: C.successBg, color: C.success }}>
              <ChevronLeft size={14} /> Guest A (Keep)
            </div>
            <div style={{ ...headerCellStyle, background: C.bgPrimary }}>Select</div>
            <div style={{ ...headerCellStyle, background: C.errorBg, color: C.error }}>
              Guest B (Delete) <ChevronRight size={14} />
            </div>

            {/* Data Rows */}
            {MERGE_FIELDS.map((field, idx) => {
              const valA = guestA[field.key] || '';
              const valB = guestB[field.key] || '';
              const isDifferent = String(valA).toLowerCase() !== String(valB).toLowerCase();
              const selectedA = selections[field.key] === 'a';
              
              return (
                <React.Fragment key={field.key}>
                  <div style={{ 
                    ...cellStyle, 
                    fontWeight: 600, 
                    color: C.textPrimary,
                    background: idx % 2 === 0 ? C.bgCard : C.bgPrimary,
                  }}>
                    {field.label}
                    {isDifferent && <span style={{ color: C.warning, marginLeft: 4 }}>*</span>}
                  </div>
                  <div 
                    onClick={() => handleSelect(field.key, 'a')}
                    style={{ 
                      ...cellStyle, 
                      cursor: 'pointer',
                      background: selectedA ? C.successBg : (idx % 2 === 0 ? C.bgCard : C.bgPrimary),
                      border: selectedA ? `2px solid ${C.success}` : '2px solid transparent',
                      fontWeight: selectedA ? 600 : 400,
                      color: C.textPrimary,
                    }}
                  >
                    {valA || <span style={{ color: C.textSecondary, fontStyle: 'italic' }}>Empty</span>}
                  </div>
                  <div style={{ 
                    ...cellStyle, 
                    display: 'flex', 
                    justifyContent: 'center',
                    background: idx % 2 === 0 ? C.bgCard : C.bgPrimary,
                  }}>
                    {selectedA ? (
                      <span style={{ color: C.success }}><Check size={18} /></span>
                    ) : (
                      <span style={{ color: C.error }}><Check size={18} /></span>
                    )}
                  </div>
                  <div 
                    onClick={() => handleSelect(field.key, 'b')}
                    style={{ 
                      ...cellStyle, 
                      cursor: 'pointer',
                      background: !selectedA ? C.errorBg : (idx % 2 === 0 ? C.bgCard : C.bgPrimary),
                      border: !selectedA ? `2px solid ${C.error}` : '2px solid transparent',
                      fontWeight: !selectedA ? 600 : 400,
                      color: C.textPrimary,
                    }}
                  >
                    {valB || <span style={{ color: C.textSecondary, fontStyle: 'italic' }}>Empty</span>}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          <div style={{ 
            marginTop: 16, 
            padding: '12px 16px', 
            background: C.warningBg, 
            borderRadius: 8,
            fontSize: 13,
            color: C.warning,
          }}>
            <strong>Note:</strong> Guest B will be deleted after merge. All communication history from both guests will be preserved on Guest A.
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button
              onClick={onClose}
              style={{
                padding: '12px 24px',
                background: C.bgPrimary,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                color: C.textPrimary,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleMerge}
              disabled={merging}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 28px',
                background: C.success,
                color: '#000',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: merging ? 0.7 : 1,
              }}
            >
              <GitMerge size={16} />
              {merging ? 'Merging...' : 'Merge & Keep Guest A'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const headerCellStyle = {
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  justifyContent: 'center',
  color: C.textSecondary,
};

const cellStyle = {
  padding: '12px 16px',
  fontSize: 13,
  borderTop: `1px solid ${C.border}`,
};

export default GuestMergeModal;
